import { useId } from 'react';

/**
 * Pencil draughts of a brig of the period, laid faintly behind a page like the shipwright's
 * sheets pinned up in a captain's cabin: the sheer plan with her masts, yards, rigging and the
 * outline of her sails, the half-breadth plan under it, the body plan beside, a scale of feet
 * and the construction lines. Drawn twice through a graphite filter with a slight offset, so
 * the lines break up and double like soft pencil. Decorative: hidden from assistive tech.
 */
export function ShipDraughts({ className }: { className?: string }) {
  const id = useId().replace(/:/g, '');
  const [draught, pencil, pencil2] = [`${id}-draught`, `${id}-pencil`, `${id}-pencil2`];
  return (
    <svg
      className={className}
      viewBox="0 0 1400 1000"
      preserveAspectRatio="xMidYMin slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {[pencil, pencil2].map((f, i) => (
          <filter key={f} id={f} x="-2%" y="-2%" width="104%" height="104%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.035"
              numOctaves="2"
              seed={3 + i * 5}
              result="warp"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="warp"
              scale={2.4 + i}
              xChannelSelector="R"
              yChannelSelector="G"
              result="wobbly"
            />
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves="1"
              seed={11 + i}
              result="grain"
            />
            <feColorMatrix
              in="grain"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 1.6"
              result="tooth"
            />
            <feComposite in="wobbly" in2="tooth" operator="in" />
          </filter>
        ))}
        <g id={draught} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <SheerPlan />
          <HalfBreadth />
          <BodyPlan />
          <Lettering />
        </g>
      </defs>
      <use href={`#${draught}`} filter={`url(#${pencil})`} />
      <use href={`#${draught}`} filter={`url(#${pencil2})`} opacity={0.45} transform="translate(0.9 0.7)" />
    </svg>
  );
}

// -- geometry -------------------------------------------------------------------

type P = readonly [number, number];
const q = (a: P, c: P, b: P, t: number): P => [
  (1 - t) ** 2 * a[0] + 2 * (1 - t) * t * c[0] + t * t * b[0],
  (1 - t) ** 2 * a[1] + 2 * (1 - t) * t * c[1] + t * t * b[1],
];
const pts = (...p: P[]) => p.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

/** The deck's sheer, stern to stem: low amidships, rising to the bow. */
const SHEER: readonly [P, P, P] = [
  [520, 432],
  [880, 470],
  [1250, 425],
];
const deckAt = (t: number) => q(...SHEER, t);
/** The deck's height under a point of the sheer plan (near enough for masts and shrouds). */
const deckY = (x: number) => deckAt((x - 520) / 730)[1];
const KEEL_Y = 560;
const LWL = 520;
const STATIONS = Array.from({ length: 13 }, (_, i) => 560 + i * 55);

/** Where the stern and the stem cut a level line at `y`. */
const sternX = (y: number) => 520 + ((y - 432) * 50) / 128;
const stemX = (y: number) => 1262 - ((y - 425) / 135) ** 2 * 82;

interface Mast {
  readonly x: number;
  /** Heads of the lower mast, topmast, topgallant and the pole's truck. */
  readonly heads: readonly [number, number, number, number];
  /** Yards: height and half length, course to royal. */
  readonly yards: readonly (readonly [number, number])[];
}
const MAIN: Mast = {
  x: 800,
  heads: [250, 150, 85, 60],
  yards: [
    [275, 130],
    [180, 100],
    [108, 68],
    [76, 46],
  ],
};
const FORE: Mast = {
  x: 1080,
  heads: [280, 180, 112, 88],
  yards: [
    [300, 122],
    [206, 94],
    [134, 64],
    [102, 44],
  ],
};
/** A mast raked aft: its x at height `y`. */
const rake = (m: Mast, y: number) => m.x - (deckY(m.x) - y) * 0.05;

function SheerPlan() {
  const [s, c, b] = SHEER;
  const off = (d: number) => `M${s[0]},${s[1] + d} Q${c[0]},${c[1] + d} ${b[0]},${b[1] + d}`;
  const ports = Array.from({ length: 8 }, (_, i) => deckAt(0.16 + i * 0.095));
  return (
    <g strokeWidth={1.3}>
      {/* the hull: rail, sheer, wales, keel, stem, sternpost and rudder */}
      <path d={off(-15)} />
      <path d={off(0)} strokeWidth={1.6} />
      <path d={off(11)} />
      <path d={off(17)} strokeWidth={0.8} />
      <path d={`M570,${KEEL_Y} L1180,${KEEL_Y} Q1255,555 1262,425 L1258,410`} strokeWidth={1.6} />
      <path d={`M570,${KEEL_Y} L520,432 L518,417`} strokeWidth={1.6} />
      <polygon points={pts([570, KEEL_Y], [546, KEEL_Y], [503, 440], [520, 432])} strokeWidth={1} />
      <path d={`M570,${KEEL_Y + 8} L1180,${KEEL_Y + 8}`} strokeWidth={0.7} />
      {ports.map(([x, y]) => (
        <rect key={x} x={x - 7} y={y - 12} width={14} height={9} strokeWidth={0.9} />
      ))}
      {/* waterlines, the load line drawn firm */}
      {[490, 505, LWL, 535, 550].map((y) => (
        <path
          key={y}
          d={`M${sternX(y)},${y} L${stemX(y)},${y}`}
          strokeWidth={y === LWL ? 1.1 : 0.6}
          strokeDasharray={y === LWL ? undefined : '7 5'}
        />
      ))}
      {/* stations, carried down to the half-breadth plan */}
      {STATIONS.map((x) => (
        <path
          key={x}
          d={`M${x},${deckY(x) - 4} L${x},${KEEL_Y + 18} M${x},612 L${x},708`}
          strokeWidth={0.45}
          strokeDasharray="2 4"
        />
      ))}
      {/* bowsprit, jib boom and the head */}
      <path d="M1236,431 L1342,383 M1312,394 L1398,356" strokeWidth={1.4} />
      <path d="M1262,425 q14,-6 10,-20 q-4,-6 -10,-2" strokeWidth={0.9} />
      {[MAIN, FORE].map((m) => (
        <Rig key={m.x} m={m} />
      ))}
      {/* the brig's spanker: gaff and boom on the main */}
      <path d={`M${rake(MAIN, 288)},288 L598,322 M${rake(MAIN, 418)},418 L496,437`} strokeWidth={1.2} />
      <polygon
        points={pts([rake(MAIN, 292), 292], [600, 324], [500, 434], [rake(MAIN, 414), 414])}
        strokeWidth={0.5}
      />
      {/* stays forward: fore, fore topmast and topgallant to the bowsprit and jib boom */}
      <path
        d={`M${rake(FORE, 284)},284 L1250,430 M${rake(FORE, 184)},184 L1340,384 M${rake(FORE, 116)},116 L1396,357`}
        strokeWidth={0.8}
      />
      <polygon points={pts([rake(FORE, 190), 190], [1334, 388], [1214, 432])} strokeWidth={0.5} />
      <polygon points={pts([rake(FORE, 122), 122], [1392, 360], [1300, 400])} strokeWidth={0.5} />
      {/* main stays to the foremast */}
      <path
        d={`M${rake(MAIN, 254)},254 L1070,${deckY(1070) - 2} M${rake(MAIN, 154)},154 L${rake(FORE, 282)},282 M${rake(MAIN, 89)},89 L${rake(FORE, 182)},182`}
        strokeWidth={0.7}
      />
    </g>
  );
}

/** A mast with its tops, yards, shrouds, backstays and the outline of its square sails. */
function Rig({ m }: { m: Mast }) {
  const [lower, top, gallant, truck] = m.heads;
  const foot = deckY(m.x);
  const x = (y: number) => rake(m, y);
  const yards = m.yards.map(([y, h]) => ({ y, h, c: x(y) }));
  return (
    <g>
      <path d={`M${m.x},${foot + 2} L${x(lower)},${lower}`} strokeWidth={2.2} />
      <path d={`M${x(lower + 14)},${lower + 14} L${x(top)},${top}`} strokeWidth={1.7} />
      <path d={`M${x(top + 10)},${top + 10} L${x(truck)},${truck}`} strokeWidth={1.2} />
      {[lower, top].map((y) => (
        <path key={y} d={`M${x(y) - 20},${y} L${x(y) + 14},${y}`} strokeWidth={1.4} />
      ))}
      <path d={`M${x(gallant)},${gallant} l-6,0`} strokeWidth={1} />
      {yards.map(({ y, h, c }) => (
        <path key={y} d={`M${c - h},${y} L${c + h},${y}`} strokeWidth={1.3} />
      ))}
      {/* square sails, each from its yard down to the one below, the course to the rail */}
      {yards.map(({ y, h, c }, i) => {
        const below = yards[i - 1];
        const [fy, fh, fc] = below ? [below.y - 6, below.h, below.c] : [foot - 34, h * 1.08, x(foot - 34)];
        return (
          <path
            key={`s${String(y)}`}
            d={`M${c - h + 3},${y + 2} L${fc - fh},${fy} Q${fc},${fy - 8} ${fc + fh},${fy} L${c + h - 3},${y + 2}`}
            strokeWidth={0.5}
          />
        );
      })}
      {/* shrouds to the channels, backstays from the topmast head further aft */}
      {[0, 1, 2, 3].map((i) => (
        <path
          key={`r${String(i)}`}
          d={`M${x(lower)},${lower + 4} L${m.x - 46 + i * 11},${foot + 6}`}
          strokeWidth={0.6}
        />
      ))}
      {[0, 1].map((i) => (
        <path
          key={`b${String(i)}`}
          d={`M${x(top)},${top + 4} L${m.x - 100 + i * 16},${foot + 8}`}
          strokeWidth={0.55}
        />
      ))}
      {/* ratlines across the lower shrouds */}
      {Array.from({ length: 9 }, (_, i) => {
        const t = 0.18 + i * 0.085;
        const y = lower + 4 + (foot + 6 - lower - 4) * t;
        const x0 = x(lower) + (m.x - 46 - x(lower)) * t;
        const x1 = x(lower) + (m.x - 13 - x(lower)) * t;
        return <path key={`l${String(i)}`} d={`M${x0},${y} L${x1},${y}`} strokeWidth={0.35} />;
      })}
    </g>
  );
}

/** The half-breadth plan: the deck and waterlines seen from above, one side of the keel. */
function HalfBreadth() {
  const cy = 708;
  const deck = `M520,${cy} L522,${cy - 46} C700,${cy - 74} 1000,${cy - 76} 1150,${cy - 56} Q1240,${cy - 34} 1264,${cy}`;
  return (
    <g strokeWidth={1}>
      <path d={`M500,${cy} L1290,${cy}`} strokeWidth={0.8} strokeDasharray="14 4 2 4" />
      {[1, 0.86, 0.72, 0.57, 0.42].map((k) => (
        <path
          key={k}
          d={deck}
          transform={`translate(890 ${cy}) scale(${0.94 + 0.06 * k} ${k}) translate(-890 ${-cy})`}
          vectorEffect="non-scaling-stroke"
          strokeWidth={k === 1 ? 1.4 : 0.7}
        />
      ))}
      {/* the masts' partners on the deck */}
      {[MAIN.x, FORE.x].map((x) => (
        <circle key={x} cx={x} cy={cy - 6} r={5} strokeWidth={0.9} />
      ))}
    </g>
  );
}

/** The body plan: sections of the forebody to the right of the middle line, afterbody left. */
function BodyPlan() {
  const [cx, deck, keel] = [250, 440, 580];
  const fore = [112, 106, 96, 80, 58, 32];
  const aft = [114, 110, 102, 90, 72, 44];
  const section = (w: number, side: 1 | -1) =>
    `M${cx},${keel} C${cx + side * w * 0.55},${keel} ${cx + side * w},${keel - 30} ${cx + side * w},${keel - 82} S${cx + side * w * 0.96},${deck + 6} ${cx + side * w * 0.9},${deck}`;
  return (
    <g strokeWidth={0.9}>
      <path d={`M${cx},${deck - 30} L${cx},${keel + 16}`} strokeWidth={0.8} strokeDasharray="14 4 2 4" />
      <path d={`M${cx - 140},${keel} L${cx + 140},${keel}`} strokeWidth={1} />
      {[490, 505, LWL, 535, 550].map((y) => (
        <path
          key={y}
          d={`M${cx - 150},${y} L${cx + 150},${y}`}
          strokeWidth={y === LWL ? 0.9 : 0.45}
          strokeDasharray={y === LWL ? undefined : '6 5'}
        />
      ))}
      {fore.map((w) => (
        <path key={`f${String(w)}`} d={section(w, 1)} />
      ))}
      {aft.map((w) => (
        <path key={`a${String(w)}`} d={section(w, -1)} />
      ))}
      {/* diagonals */}
      <path
        d={`M${cx},${deck + 10} L${cx + 150},${keel - 10} M${cx},${deck + 10} L${cx - 150},${keel - 10}`}
        strokeWidth={0.45}
      />
      {/* compass sweeps the sections were struck with */}
      <path
        d={`M${cx + 118},${keel - 140} A160,160 0 0 1 ${cx + 30},${keel + 20}`}
        strokeWidth={0.4}
        strokeDasharray="3 6"
      />
      <path
        d={`M${cx - 120},${keel - 150} A170,170 0 0 0 ${cx - 36},${keel + 18}`}
        strokeWidth={0.4}
        strokeDasharray="3 6"
      />
    </g>
  );
}

function Lettering() {
  const feet = Array.from({ length: 11 }, (_, i) => 560 + i * 34);
  return (
    <g strokeWidth={0.8}>
      {/* the scale of feet */}
      <path d={`M560,812 L900,812 M560,818 L900,818`} />
      {feet.map((x) => (
        <path key={x} d={`M${x},808 L${x},822`} />
      ))}
      {/* length on deck, between perpendiculars */}
      <path d="M520,598 L1262,598 M520,592 L520,604 M1262,592 L1262,604 M530,594 L520,598 L530,602 M1252,594 L1262,598 L1252,602" />
      {/* a pencilled note in the margin */}
      <path d="M940,942 q30,-10 60,0 t60,0 t60,0 t60,0" strokeWidth={0.6} />
    </g>
  );
}
